import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { StringKeyMap } from '../../../adl-gen/common';
import { DbKey, makeWithDbId, WithDbId } from '../../../adl-gen/common/db';
import {
  AppUser,
  // CompetencySkillTrainingOffering,
  TrainingOffering,
  TrainingOfferingCategory
} from '../../../adl-gen/totalinteraction/futurefit/db';
import {
  Recommendation,
  RecommendationResult,
  SingleUserTrainingRecommendations
} from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { i18next } from '../../../internationalization/i18next';
import { CategoryOption, LearningType } from '../../../stores/quiz-results-store';
import { renderTemplate } from '../../../util/utils';
import { MarkdownContent } from '../../widgets/markdown-content/markdown-content';
import { SelectOption } from '../../widgets/select/select';
import { Modal } from '../modal/modal';
import { RecommendationTile, TileAccordions } from '../tile-accordions/tile-accordions';
import { Tile } from '../tile/tile';

const styles = require('./recommendations.css');

interface RecommendationsProps {
  userTraining: SingleUserTrainingRecommendations;
  // If set, these are used for tabs and accordions in output (where TileAccordion is 'recommendation-external')
  categories?: CategoryOption[];
  // 'external' means use categories for tabs
  // 'none' means don't use any tabs in output
  // TrainingOfferingCategory means use that category for tabs
  categoryFilter?: TrainingOfferingCategory | 'external' | 'external-controlled' | 'none';
  // Recommendation tag -> Tile subtitle -> Pill
  tags?: SelectOption[];
  /// Which accordions to display
  priorityFilter?: LearningType[];
  enabled: boolean;
  launchUrl?(offering: WithDbId<TrainingOffering>, userId: DbKey<AppUser>): string;
}

@observer
export class Recommendations extends React.Component<RecommendationsProps> {
  @observable offeringSelected: string | undefined;
  /** Renders the component - new version styled as columns of skill 'cards' */

  @action selectTile = (offeringId: string) => {
    this.offeringSelected = offeringId;
  };

  @action deselectTile = () => {
    this.offeringSelected = undefined;
  };

  filteredTrainingOnCategory = (
    userRecs: SingleUserTrainingRecommendations,
    c: TrainingOfferingCategory
  ): SingleUserTrainingRecommendations => {
    const filteredData: StringKeyMap<DbKey<TrainingOffering>, TrainingOffering> = {};
    let filteredRecommendations: Recommendation[] = [];
    // There shouldn't be any need to filter the CompetencySkillMap at all
    // let filteredCompetencySkillMap: StringKeyMap<DbKey<TrainingOffering>, CompetencySkillTrainingOffering[]> = {};
    for (const k of Object.keys(userRecs.data)) {
      if (userRecs.data[k].category === c) {
        filteredData[k] = userRecs.data[k];
      }
    }
    filteredRecommendations = userRecs.user.recommendations.filter(r =>
      Object.keys(filteredData).find(k => k === r.trainingOfferingId)
    );
    // for (const k of Object.keys(utoData.competencySkillMap)) {
    //   if (filteredRecommendations.map(rec => rec.trainingOfferingId).includes(k)) {
    //     filteredCompetencySkillMap[k] = utoData.competencySkillMap[k];
    //   }
    // }
    return {
      user: {
        ...userRecs.user,
        recommendations: filteredRecommendations
      },
      data: filteredData
    };
  };

  renderRecommendationTilesDirectly(recommendationTiles: RecommendationTile[]): JSX.Element {
    if (recommendationTiles.length > 0) {
      return <div className={styles.content}>{recommendationTiles.map(this.renderRecommendationTileDirectly)}</div>;
    }
    return <></>;
  }

  renderRecommendationTileDirectly(recommendationTile: RecommendationTile): JSX.Element {
    return (
      <div key={recommendationTile.url} className={styles.tileContainer}>
        <Tile
          title={recommendationTile.title}
          subtitle={recommendationTile.tag}
          imageUrl={recommendationTile.imageUrl}
          key={recommendationTile.url}
          kind={'recommendation'}
          onClick={recommendationTile.onClick}
          enabled={recommendationTile.enabled}
          completed={recommendationTile.completed}
        />
      </div>
    );
  }

  renderRecommendationsInternalCategory(): JSX.Element | null {
    const { categoryFilter, priorityFilter, tags, userTraining } = this.props;
    let filteredTraining = userTraining;
    if (
      categoryFilter !== undefined &&
      categoryFilter !== 'external' &&
      categoryFilter !== 'external-controlled' &&
      categoryFilter !== 'none'
    ) {
      /// TODO: This is probably too restrictive as it doesn't need to restrict the data field as well - so don't pass to renderModals
      filteredTraining = this.filteredTrainingOnCategory(userTraining, categoryFilter);
    }

    /// Fix issue: Recs are not sorted in order of priority (desc)
    /// Copied from slice() to silence MobX warnings
    /// possible TODO rework to ensure total of 6 items in 'Highly Recommended' accordion across all categories
    /// (This solution will potentially provide up to 18 'Highly Recommended' offerings regardless of scores)
    /// Recommendation methods: Do this only for RELEVANCE recommendations

    const sortedRecommendations: Recommendation[] = filteredTraining.user.recommendations.slice().sort((a, b) => {
      if (a.result.kind === 'priority' && b.result.kind === 'priority') {
        return b.result.value - a.result.value;
      } else {
        return 0;
      }
    });

    /// SS-746 selectTile is meant to set the offeringSelected flag, which is used by renderModals to decide which modal to open.
    /// However, for allLearning, this doesn't work, because it will only open a modal for a training offering which is in the set of
    /// Recommendations, not one that is missing from that list but included in allLearning.
    const allLearning: RecommendationTile[] =
      /// SS-759 All-Learning enabled but we want to ensure the category filter still applies
      // Object.entries(userTraining.data)
      Object.entries(filteredTraining.data)
        .map(([k, v]) => makeWithDbId({ id: k, value: v }))
        .slice()
        .sort((a, b) => a.value.title.localeCompare(b.value.title))
        .map(off =>
          offeringToTile(
            userTraining,
            off.id,
            { kind: 'priority', value: 0 },
            this.props.enabled,
            false,
            () => this.selectTile(off.id),
            tags || []
          )
        );

    return (
      <>
        <TileAccordions
          kind={'recommendation-category'}
          selectedPriorities={priorityFilter}
          recommendationTiles={sortedRecommendations.map(r =>
            offeringToTile(
              filteredTraining,
              r.trainingOfferingId,
              r.result,
              this.props.enabled,
              r.completed,
              () => this.selectTile(r.trainingOfferingId),
              tags || []
            )
          )}
          allAvailableTiles={allLearning}
        />
        {this.renderModals(userTraining)}
      </>
    );
  }

  renderRecommendationsExternalCategories(
    kind: 'recommendation-external' | 'recommendation-external-controlled'
  ): JSX.Element | null {
    const { categories, priorityFilter, tags, userTraining } = this.props;

    const allLearning: RecommendationTile[] = Object.entries(userTraining.data)
      .map(([k, v]) => makeWithDbId({ id: k, value: v }))
      .slice()
      .sort((a, b) => a.value.title.localeCompare(b.value.title))
      .map(off =>
        offeringToTile(
          userTraining,
          off.id,
          /// This is not used, so default to priority
          { kind: 'priority', value: 0 },
          this.props.enabled,
          false,
          () => this.selectTile(off.id),
          tags || []
        )
      );

    return (
      <>
        <TileAccordions
          kind={kind}
          categoryMap={userTraining.user.competencySkillMap}
          categories={categories || []}
          selectedPriorities={priorityFilter}
          recommendationTiles={userTraining.user.recommendations.map(r =>
            offeringToTile(
              userTraining,
              r.trainingOfferingId,
              r.result,
              this.props.enabled,
              r.completed,
              () => this.selectTile(r.trainingOfferingId),
              tags || []
            )
          )}
          allAvailableTiles={allLearning}
        />
        {this.renderModals(userTraining)}
      </>
    );
  }

  renderRecommendationsNoCategories(): JSX.Element | null {
    const { tags, userTraining } = this.props;
    return (
      <>
        {this.renderRecommendationTilesDirectly(
          userTraining.user.recommendations.map(r =>
            offeringToTile(
              userTraining,
              r.trainingOfferingId,
              r.result,
              this.props.enabled,
              r.completed,
              () => this.selectTile(r.trainingOfferingId),
              tags || []
            )
          )
        )}
        {this.renderModals(userTraining)}
      </>
    );
  }

  render(): JSX.Element | null {
    switch (this.props.categoryFilter) {
      case 'external':
        return this.renderRecommendationsExternalCategories('recommendation-external');
      case 'external-controlled':
        return this.renderRecommendationsExternalCategories('recommendation-external-controlled');
      case 'none':
        return this.renderRecommendationsNoCategories();
      default:
        return this.renderRecommendationsInternalCategory();
    }
  }

  /// SS-746: Run this off *all* data not recommendations, because otherwise clicking on a tile in the All Available Learning section may do nothing
  renderModals(training: SingleUserTrainingRecommendations) {
    const data = Object.entries(training.data);
    return (
      <>
        {data.map(([k, v]) => {
          // Not training.user.recommendations.map(r => ...)
          const offering: WithDbId<TrainingOffering> = makeWithDbId({ id: k, value: v });
          /// get the "executeUrl" we are actually using in the Markdown section
          ///  - real "executeUrl" (eg.running as manager), or
          //   - phone-home-first URL, which goes to the server instead - then the server sends the "real" executeUrl with a 303 See Other status
          const launchUrl = this.props.launchUrl
            ? this.props.launchUrl(offering, training.user.appUserId)
            : offering.value.executeUrl;
          return (
            <Modal
              key={offering.id}
              isOpen={offering.id === this.offeringSelected}
              onRequestClose={this.deselectTile}
              shouldCloseOnOverlayClick={true}
              shouldCloseOnEsc={true}
            >
              <div className={styles.title}>{offering.value.title}</div>
              <div className={styles.description}>
                <MarkdownContent
                  text={renderTemplate(offering.value.description, {
                    TrainingOffering: { ...offering.value, executeUrl: launchUrl }
                  })}
                />
              </div>
            </Modal>
          );
        })}
      </>
    );
  }
}

function categoryToString(category: TrainingOfferingCategory): string {
  switch (category) {
    case TrainingOfferingCategory.elearning:
      return i18next.t('tile-accordions.recommendations-categories.elearning', 'E-Learning');
    case TrainingOfferingCategory.face2face:
      return i18next.t('tile-accordions.recommendations-categories.face2face', 'Face to Face');
    case TrainingOfferingCategory.virtual:
      return i18next.t('tile-accordions.recommendations-categories.virtual', 'Virtual');
  }
}

/// Use competency name for tag (pill) - but fall back to category if it's unavailable
export function offeringToTile(
  training: SingleUserTrainingRecommendations,
  id: DbKey<TrainingOffering>,
  result: RecommendationResult,
  enabled: boolean,
  completed: boolean,
  onClick: () => void,
  tags: SelectOption[]
): RecommendationTile {
  const offering: TrainingOffering = training.data[id];

  return {
    tag: getTag(result, tags) || categoryToString(offering.category).toUpperCase(),
    url: offering.executeUrl,
    imageUrl: offering.tileUrl,
    title: offering.title,
    key: id,
    result,
    enabled,
    completed,
    onClick
  };
}

// /// Used to put Competency on Pill
// function offeringToTileWithExternalTags(
//   training: UserTrainingOfferingsData,
//   id: DbKey<TrainingOffering>,
//   result: RecommendationResult,
//   enabled: boolean,
//   completed: boolean,
//   onClick: () => void,
//   tags: SelectOption[]
// ): RecommendationTile {
//     const offering: TrainingOffering = training.data[id];
//     return {
//       tag: getTag(training, id, tags) || categoryToString(offering.category).toUpperCase(),
//       url: offering.executeUrl,
//       imageUrl: offering.tileUrl,
//       title: offering.title,
//       key: id,
//   // Note: This is not used
//       result,
//       enabled,
//       completed,
//       onClick,
//     }
// }

/// Tags is a SelectOption array, with the Label = Competency fullname, and the value is either the Skill or Competency ID
function getTag(
  // training: UserTrainingOfferingsData,
  // id: DbKey<TrainingOffering>,
  result: RecommendationResult,
  tags: SelectOption[]
): string | undefined {
  /// options -> undefined: kind = 'priority', tags are empty, or Id isn't in list of tags.
  if (result.kind === 'roleScore') {
    const displayOption = tags.find(option => option.value === result.value.skillOrCompetency);
    if (displayOption) {
      return displayOption.label.toUpperCase();
    } else {
      return undefined;
    }
  }
  return undefined;
  //  const associatedCompetencyId = getAssociatedCompetencyOrSkill(training, id);
  //   // console.log("DEBUG getTag associatedCompetencyId, tag values", associatedCompetencyId, tags.map(opt => opt.value).join(","));
  //   if (associatedCompetencyId !== undefined) {
  //     const displayName = tags.find(option => option.value === associatedCompetencyId);
  //     if (displayName !== undefined) {
  //       return displayName.label.toUpperCase();
  //     }
  //   }
  //   return undefined;
}

// /// Gets competency or skill ID from competencySkillMap
// /**
//  * @deprecated This fails because it picks the first one found - but there could easily be others, so any subsequent sort on Competency or Skill
//  *             will mismatch
//  */
// function getAssociatedCompetencyOrSkill(
//   training: UserTrainingOfferingsData,
//   id: DbKey<TrainingOffering>
// ): string | undefined {
//   for ( const [k, v] of Object.entries(training.competencySkillMap)) {
//     // console.log("DEBUG getAssocCompetencyOrSkill id, traininOfferings", k,  ",", v.map(item => item.value.trainingOfferingId).join(","));
//     const refId = v //.filter(corsto => corsto.kind === "competencyTrainingOffering")
//     .find(corsto => corsto.value.trainingOfferingId === id);
//     // console.log("DEBUG getAssocCompetencyOrSkill traininOffering found", refId);
//     if (refId !== undefined) {
//       return k;
//     // } else {
//     //   /// Try indirectly via skills
//     //   refId = v.filter(corsto => corsto.kind === "skillTrainingOffering").find(corsto => corsto.value.trainingOfferingId === id);
//     //   if (refId !== undefined) {
//     //     ...
//     //   }
//     }
//   }
//   return undefined;
// }



// WEBPACK FOOTER //
// ./src/ui/components/recommendations/recommendations.tsx